import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { SessionService } from '../../core/services/session.service';
import { PreserveRouteService } from '../../core/services/preserveRoute.service';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotificationService } from '@app/core/services/notification.service';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { TranslateService } from '@ngx-translate/core';
import { IConfirmConfig } from '@app/core/types/dialog.type';

@Component({
    selector: 'itfg-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    standalone: false
})
export class LoginFormComponent implements OnInit, OnDestroy {
  loginErrored: boolean;

  loginForm: UntypedFormGroup;
  session: SessionService;
  preserveService: PreserveRouteService;
  notification: NotificationService;
  dialogService: DialogProviderService;
  translateService: TranslateService;
  router: Router;
  showForgotPassword: boolean = false;
  _unsubscribe: Subject<void> = new Subject<void>();
  showPasswordManagerSuggestion: boolean = false;
  private passwordKeyEvents: number[] = [];

  constructor(
    session: SessionService,
    router: Router,
    preserveService: PreserveRouteService,
    notification: NotificationService,
    dialogService: DialogProviderService,
    translateService: TranslateService
  ) {
    this.loginErrored = false;
    this.session = session;
    this.router = router;
    this.preserveService = preserveService;
    this.notification = notification;
    this.dialogService = dialogService;
    this.translateService = translateService;

    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl(),
      password: new UntypedFormControl(),
    });
  }

  ngOnInit() {
    this.session.administratorLoaded
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        const lastVisitedRoute = this.session.getLastVisitedRoute();
        this.router.navigateByUrl(lastVisitedRoute);
      });
  }

  onPasswordKeydown(event: KeyboardEvent): void {
    this.passwordKeyEvents.push(Date.now());
  }

  onPasswordInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (!value) {
      this.showPasswordManagerSuggestion = false;
      this.passwordKeyEvents = [];

      return;
    }

    // detect autofill
    if (this.passwordKeyEvents.length >= 2) {
      const timeDiff =
        this.passwordKeyEvents[this.passwordKeyEvents.length - 1] -
        this.passwordKeyEvents[0];
      const isRapidInput = timeDiff < 100;

      if (isRapidInput) {
        this.showPasswordManagerSuggestion = false;
        this.passwordKeyEvents = [];

        return;
      }
    }

    // show suggestion after 4 characters and 3 keystrokes
    if (value.length >= 4 && this.passwordKeyEvents.length >= 3) {
      this.showPasswordManagerSuggestion = true;
    }
  }

  onPasswordPaste(): void {
    this.showPasswordManagerSuggestion = false;
  }

  onSubmit(event): void {
    if (this.loginForm.valid) {
      this.loginErrored = false;

      this.session.login(this.loginForm.value).subscribe({
        next: (res) => {
          if (res === 423) {
            const dialogConfig: IConfirmConfig = {
              message: this.translateService.instant(
                'administrators.lockedAdministratorProfileMessage'
              ),
              disableClose: true,
              title: this.translateService.instant(
                'administrators.lockedAdministratorProfileTitle'
              ),
            };

            this.dialogService.openInfo(dialogConfig);
          }
          if (!res) {
            this.loginErrored = true;
          }
        },
      });
    }
  }

  onForgotPasswordClick(): void {
    this.showForgotPassword = !this.showForgotPassword;
    if (this.showForgotPassword) {
      this.loginForm.get('password').disable();
    } else {
      this.loginForm.get('password').enable();
    }
  }

  sendPasswordResetEmail(event): void {
    event.preventDefault();
    const email = this.loginForm.get('username')?.value;
    if (email) {
        this.session.sendPasswordResetEmail(email)
            .subscribe({
              next: () => {
                this.notification.showLocalizedSuccessMessage({
                  notificationText: 'auth.resetPasswordEmailSuccessMessage',
                });
              },
              error: () => {
                this.notification.showLocalizedErrorMessage({
                  notificationText: 'global.somethingWentWrong',
                });
              }
            });
    } else {
      this.notification.showLocalizedErrorMessage({
        notificationText: 'auth.provideEmailMessage',
      });
    }
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
